/**
 * Text Writing Animation Script
 * Triggers a staggered writing animation on elements with [data-text-anim="write"]
 * when they enter the viewport using IntersectionObserver.
 */
(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.TextAnimInitialized) return;
  window.TextAnimInitialized = true;

  // Inject required CSS rules dynamically
  const style = document.createElement('style');
  style.id = 'text-anim-styles';
  style.textContent = `
    /* Avoid FOUC: hide elements in JS-enabled browsers before preparation is complete */
    .js [data-text-anim]:not(.anim-prepared) {
      opacity: 0 !important;
    }
    
    .anim-char {
      display: inline-block;
      opacity: 0;
      transition-property: opacity;
      transition-duration: 0.18s;
      transition-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    
    /* Keep space characters as inline so browser wrapping works naturally */
    .anim-char--space {
      display: inline !important;
      white-space: pre-wrap;
    }
    
    [data-text-anim].anim-started .anim-char {
      opacity: 1;
    }
    
    /* Fade Up Animation Styles */
    [data-text-anim="fade-up"] .anim-char {
      transform: translateY(0.25em);
      transition-property: opacity, transform;
      transition-duration: 0.4s;
      transition-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    
    [data-text-anim="fade-up"].anim-started .anim-char {
      transform: translateY(0);
    }
  `;
  document.head.appendChild(style);

  /**
   * Recursively processes an element's child nodes, splitting text nodes into character spans.
   * Maintains a global character index to apply sequential delays and handles whitespace collapsing.
   * @param {Node} node 
   * @param {Object} state - Keeps track of the character count and spacing state
   */
  function splitTextNodes(node, state) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      const fragment = document.createDocumentFragment();
      
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const isWhitespace = (char === ' ' || char === '\t' || char === '\n' || char === '\r');
        
        if (isWhitespace) {
          if (state.charIndex > 0) {
            state.pendingSpace = true;
          }
        } else {
          if (state.pendingSpace) {
            // Create a single space span and assign delay
            const spaceSpan = document.createElement('span');
            spaceSpan.textContent = ' ';
            spaceSpan.className = 'anim-char anim-char--space';
            spaceSpan.style.transitionDelay = (state.charIndex * state.speed) + 'ms';
            fragment.appendChild(spaceSpan);
            state.charIndex++;
            state.pendingSpace = false;
          }
          
          const span = document.createElement('span');
          span.textContent = char;
          span.className = 'anim-char';
          span.style.transitionDelay = (state.charIndex * state.speed) + 'ms';
          fragment.appendChild(span);
          state.charIndex++;
        }
      }
      
      return fragment.childNodes.length > 0 ? fragment : null;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Do not process existing animation spans or script/style tags
      if (node.classList.contains('anim-char') || ['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT'].includes(node.tagName)) {
        return null;
      }
      const children = Array.from(node.childNodes);
      for (const child of children) {
        const replacement = splitTextNodes(child, state);
        if (replacement) {
          node.replaceChild(replacement, child);
        } else if (child.nodeType === Node.TEXT_NODE) {
          // Remove empty or purely ignored text nodes to keep DOM clean
          node.removeChild(child);
        }
      }
    }
    return null;
  }

  /**
   * Prepares the element for animation by recursively wrapping text nodes and adding classes.
   * @param {HTMLElement} element 
   */
  function prepareElement(element) {
    if (element.classList.contains('anim-prepared')) return;

    const speed = parseInt(element.getAttribute('data-anim-speed'), 10) || 30; // default 30ms
    const state = { charIndex: 0, speed: speed, pendingSpace: false };
    
    splitTextNodes(element, state);
    element.classList.add('anim-prepared');
  }

  /**
   * Initializes observer and prepares target elements
   */
  function initTextAnimations(root = document) {
    const targets = root.querySelectorAll('[data-text-anim]');
    if (targets.length === 0) return;

    // Set up the IntersectionObserver
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -8% 0px', // trigger slightly before it reaches the viewport center
      threshold: 0.05
    };

    const observer = new IntersectionObserver((entries, self) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          element.classList.add('anim-started');
          self.unobserve(element); // Trigger once (no loop)
        }
      });
    }, observerOptions);

    targets.forEach(element => {
      prepareElement(element);
      // Observe the element if it hasn't already been started
      if (!element.classList.contains('anim-started')) {
        observer.observe(element);
      }
    });
  }

  // Run on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTextAnimations());
  } else {
    initTextAnimations();
  }

  // Support Shopify Customizer Section Load/Render
  document.addEventListener('shopify:section:load', (event) => {
    initTextAnimations(event.target);
  });
})();
